import { useAbility } from '../../../../../app/casl/AbilityContext';
import {
  canAccessMenuItem,
  systemUserBottomMenuConfig,
  systemUserMenuConfig,
  type MenuItemConfig,
  type SidebarHighlight,
} from '../../../../../app/config/menuConfig';
import { useMenuBadgeCounts } from '../../../../../app/hooks/useMenuBadgeCounts';
import { useVersionCheck } from '../../../../../app/hooks/useVersionCheck';
import { useGettingStartedWidget } from '../../../../../app/pages/home/components/setup-guide/GettingStartedWidgetContext';
import { KTIcon } from '../../../../helpers';
import { SidebarMenuItem } from './SidebarMenuItem';
import { SidebarMenuItemWithSub } from './SidebarMenuItemWithSub';

declare const __APP_VERSION__: string;

const AdminSideBar = () => {
  const ability = useAbility();
  const badgeCounts = useMenuBadgeCounts();
  const { hasNewVersion, newVersion, hardRefresh } = useVersionCheck({ checkInterval: 60000 });

  const renderMenuItem = (item: MenuItemConfig): JSX.Element | null => {
    // If item has children, render as submenu
    if (item.children && item.children.length > 0) {
      // Filter children that have visible content (recursively)
      const visibleChildren = item.children.filter((child) => canAccessMenuItem(child, ability));

      // Don't render if no visible children
      if (visibleChildren.length === 0) {
        return null;
      }

      return (
        <SidebarMenuItemWithSub
          key={item.to}
          fontIcon={item.fontIcon}
          hasBullet={item.hasBullet}
          icon={item.icon}
          title={item.title}
          to={item.to}
          external={item.external}
        >
          {visibleChildren.map((child) => renderMenuItem(child))}
        </SidebarMenuItemWithSub>
      );
    }

    // Check permission for leaf items
    if (!canAccessMenuItem(item, ability)) {
      return null;
    }

    const badgeCount = item.badgeCountKey ? badgeCounts[item.badgeCountKey] : 0;
    const countHighlight: SidebarHighlight = { variant: 'count', label: String(badgeCount) };
    const highlight = badgeCount > 0 ? countHighlight : item.highlight;

    // Render as regular menu item
    return (
      <SidebarMenuItem
        key={item.to}
        external={item.external}
        fontIcon={item.fontIcon}
        hasBullet={item.hasBullet}
        highlight={highlight}
        icon={item.icon}
        title={item.title}
        to={item.to}
      />
    );
  };

  return (
    <div className="d-flex flex-column justify-content-between h-100 position-relative">
      <div className="row">{systemUserMenuConfig.map((item) => renderMenuItem(item))}</div>

      <div className="row ">
        <div className="pb-4 ps-0">
          {systemUserBottomMenuConfig.map((item) => renderMenuItem(item))}

          {/* Version indicator */}
          <div className="d-flex justify-content-center mt-3 mb-5">
            {hasNewVersion ? (
              <button
                className="btn btn-sm btn-light-success d-flex align-items-center gap-2 px-3 py-2 rounded-pill border-0"
                title={`Atualizar para versão ${newVersion}`}
                type="button"
                onClick={hardRefresh}
              >
                <i className="bi bi-arrow-repeat fs-6" />
                <span className="fw-semibold fs-8">Atualizar</span>
                <span className="badge badge-success fs-9">{newVersion}</span>
              </button>
            ) : (
              <span className="badge badge-light-secondary fs-8 px-3 py-2 rounded-pill">
                <i className="bi bi-check-circle me-1 text-success" />
                {__APP_VERSION__}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AdminSideBar };
